import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MapPin, Instagram, Facebook, Heart, Phone, Mail } from 'lucide-react';

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();

  const createWhatsAppLink = (phone: string, message: string) => {
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${phone.replace(/\D/g, '')}?text=${encodedMessage}`;
  };

  const getWhatsAppContact = () => {
    if (i18n.language === 'id') {
      return {
        name: 'Nila',
        phone: '+62 813 3732 9299',
        message: 'Hi Nila! Saya tertarik dengan layanan TN Cakes Bali®. Bisakah Anda memberikan informasi lebih lanjut?'
      };
    } else {
      return {
        name: 'Tony',
        phone: '+61 401 461 019',
        message: 'Hi Tony! I\'m interested in TN Cakes Bali® services. Could you please provide more information?'
      };
    }
  };

  const whatsappContact = getWhatsAppContact();

  return (
    <footer className="bg-gray-100 text-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          
          {/* Left Column: Brand & Contact */}
          <div className="space-y-6">
            {/* Brand Section */}
            <div>
              <div className="flex items-center mb-4">
                <img
                  src="/logo.webp"
                  alt="TN Cakes Bali"
                  width={249}
                  height={60}
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <p className="text-gray-600 text-sm leading-relaxed max-w-md">
                Custom cakes for weddings, birthdays, and special moments.
              </p>
            </div>

            {/* Contact Information */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-red-500 flex-shrink-0" />
                <span className="text-gray-600 text-sm">Jl. Dewi Saraswati No.9, Seminyak, Bali</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span className="text-gray-600 text-sm">
                  {i18n.language === 'id' ? '+62 813 3732 9299 (Nila)' : '+61 0401461019 (Tony)'}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <span className="text-gray-600 text-sm">admin@tncakesbali.com</span>
              </div>
            </div>

            {/* Social Media & Contact */}
            <div className="flex items-center space-x-4">
              <a
                href="https://www.instagram.com/tncakesbali"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gradient-to-br from-pink-500 to-red-500 rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300"
                aria-label="Instagram"
              >
                <Instagram className="h-4 w-4 text-white" />
              </a>
              <a
                href="https://www.facebook.com/tncakes.bali/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300"
                aria-label="Facebook"
              >
                <Facebook className="h-4 w-4 text-white" />
              </a>
              <a
                href="https://share.google/GAMnSlD6utHIr0td0"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300"
                aria-label="View on Google Maps"
              >
                <MapPin className="h-4 w-4 text-white" />
              </a>
              <a
                href="https://www.tiktok.com/@tncakesbali7"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gradient-to-br from-black to-gray-700 rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300"
                aria-label="TikTok"
              >
                <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M16.5 3c.3 1.9 1.6 3.5 3.5 4v3.1c-1.6-.1-3.1-.6-4.5-1.4v6.3c0 3.9-3.2 7-7.1 7-1.6 0-3.1-.5-4.3-1.4C2.6 19.4 2 17.9 2 16.3c0-3.9 3.2-7.1 7.1-7.1.6 0 1.1.1 1.6.2v3.5c-.5-.2-1-.3-1.6-.3-2 0-3.6 1.6-3.6 3.7s1.6 3.7 3.6 3.7 3.6-1.6 3.6-3.7V3h3.8z"/>
                </svg>
              </a>
              <a
                href={createWhatsAppLink(whatsappContact.phone, whatsappContact.message)}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300"
                aria-label={`WhatsApp ${whatsappContact.name}`}
              >
                <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Right Column: Navigation & Legal */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* Navigation */}
            <div>
              <h3 className="font-semibold text-lg mb-4 text-burgundy-600">Navigation</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/" className="text-gray-600 hover:text-burgundy-600 transition-colors text-sm">
                    {t('nav.home')}
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="text-gray-600 hover:text-burgundy-600 transition-colors text-sm">
                    {t('nav.about')}
                  </Link>
                </li>
                <li>
                  <Link to="/charity" className="text-gray-600 hover:text-burgundy-600 transition-colors text-sm">
                    {t('nav.charity')}
                  </Link>
                </li>
                <li>
                  <Link to="/for-business" className="text-gray-600 hover:text-burgundy-600 transition-colors text-sm">
                    {t('nav.forBusiness')}
                  </Link>
                </li>
                <li>
                  <Link to="/reviews" className="text-gray-600 hover:text-burgundy-600 transition-colors text-sm">
                    {t('nav.reviews')}
                  </Link>
                </li>
                <li>
                  <Link to="/portfolio" className="text-gray-600 hover:text-burgundy-600 transition-colors text-sm">
                    {t('nav.portfolio')}
                  </Link>
                </li>
                <li>
                  <Link to="/inquiry" className="text-gray-600 hover:text-burgundy-600 transition-colors text-sm">
                    {t('nav.inquiry')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal & Additional */}
            <div>
              <h3 className="font-semibold text-lg mb-4 text-burgundy-600">Legal</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/terms" className="text-gray-600 hover:text-burgundy-600 transition-colors text-sm">
                    Terms & Privacy
                  </Link>
                </li>
                <li>
                  <a 
                    href={createWhatsAppLink(whatsappContact.phone, whatsappContact.message)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-burgundy-600 transition-colors text-sm"
                  >
                    WhatsApp {whatsappContact.name}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Copyright */}
        <div className="border-t border-gray-300 mt-8 pt-6">
          <div className="flex flex-col md:flex-row justify-center items-center space-y-2 md:space-y-0">
            <p className="text-gray-500 text-sm text-center">
              {t('footer.copyright')}
            </p>
            <div className="flex items-center space-x-1 text-gray-500 text-sm md:ml-6">
              <span>Designed with</span>
              <Heart className="h-3 w-3 text-red-500 fill-current" />
              <span>by</span>
              <a
                href="https://madebyrama.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-burgundy-600 hover:text-burgundy-500 transition-colors font-medium"
              >
                MadebyRama
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
